import { ReactNode } from 'react';

import { Box, Button, ButtonOwnProps, useTheme } from '@mui/material';

export const sizeStyles = {
  default: {
    fontSize: '14px',
    padding: '8px 10px',
    lineHeight: '16px',
    height: '36px',
    gap: '8px',
  },
  xSmall: {
    fontSize: '14px',
    padding: '4px 8px',
    lineHeight: '16px',
    height: '28px',
    gap: '4px',
  },
  small: {
    fontSize: '14px',
    padding: '6px 8px',
    lineHeight: '16px',
    height: '32px',
    gap: '6px',
  },
  big: {
    fontSize: '18px',
    padding: '8px 10px',
    lineHeight: '20px',
    height: '44px',
    gap: '8px',
  },
} as const;

export type ButtonSize = keyof typeof sizeStyles;

type BasicButtonProps = ButtonOwnProps & {
  title?: string;
  onClick?: () => void;
  children?: ReactNode | [ReactNode, ReactNode];
  sizeVariant?: ButtonSize;
  type?: 'button' | 'submit' | 'reset';
  value?: string;
  form?: string;
};

export const BasicButton = ({
  title,
  children,
  onClick,
  sx,
  sizeVariant = 'default',
  value,
  form,
  ...props
}: BasicButtonProps) => {
  const childrenArray = Array.isArray(children)
    ? children
    : children
    ? [children]
    : [];

  const [startIcon, endIcon] = childrenArray;
  const sizeStyle = sizeStyles[sizeVariant];
  const theme = useTheme();

  const hasOnlyOneChild = !title && childrenArray.length === 1;
  const isBigSize = sizeVariant === 'big';

  const paddingValue =
    hasOnlyOneChild && !isBigSize
      ? sizeStyle.padding.split(' ')[0]
      : sizeStyle.padding;

  return (
    <Button
      {...props}
      value={value}
      form={form}
      onClick={onClick}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '500',
          textTransform: 'none',
          fontSize: sizeStyle.fontSize,
          lineHeight: sizeStyle.lineHeight,
          height: sizeStyle.height,
          padding: paddingValue,
          width: 'fit-content',
          minWidth: 'auto',
          '&.Mui-disabled': {
            color: theme.text?.disabled2,
            border: theme.background?.surfaceDark,
            cursor: 'not-allowed',
            '& svg path': {
              fill: theme.text?.disabled2,
            },
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={sizeStyle.gap}
      >
        {startIcon && (
          <Box display="flex" alignItems="center">
            {startIcon}
          </Box>
        )}
        {title && (
          <Box
            sx={{
              fontSize: sizeStyle.fontSize,
              lineHeight: sizeStyle.lineHeight,
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Box>
        )}
        {endIcon && (
          <Box display="flex" alignItems="center">
            {endIcon}
          </Box>
        )}
      </Box>
    </Button>
  );
};

export default BasicButton;
